import { useTranslation } from "react-i18next";
import { Search, X, Zap, Sparkles, TrendingUp, Users } from "lucide-react";
import { cn } from "../../lib/utils";
import type { CompanionMessage } from "../../lib/companionGuide";
import CompanionBanner from "../companion/CompanionBanner";

interface HeroSectionProps {
  isAuthenticated: boolean;
  onTabChange?: (tab: string) => void;
  companionMessages?: CompanionMessage[];
  onDismissCompanion?: (id: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isAiSearch?: boolean;
  onAiSearchToggle?: (enabled: boolean) => void;
  onSearchSubmit?: () => void;
}

export const HeroSection = ({
  isAuthenticated,
  onTabChange,
  companionMessages,
  onDismissCompanion,
  searchValue = "",
  onSearchChange,
  isAiSearch = false,
  onAiSearchToggle,
  onSearchSubmit,
}: HeroSectionProps) => {
  const { t } = useTranslation();
  const tip = companionMessages?.[0] ?? null;

  /* ── Shared search bar ─────────────────────────────────────────────── */
  const renderSearchBar = (size: "sm" | "lg") => {
    const isLarge = size === "lg";
    return (
      <div
        className={cn(
          "relative flex items-center transition-all duration-300",
          isLarge ? "rounded-2xl" : "rounded-2xl",
          isAiSearch
            ? "shadow-[0_0_0_1px_hsl(var(--primary)/0.3),0_4px_16px_-2px_hsl(var(--primary)/0.12)]"
            : "shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_2px_8px_-2px_rgba(0,0,0,0.06),0_8px_24px_-4px_rgba(0,0,0,0.04)]",
        )}
        style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <Search className={cn(
          "absolute pointer-events-none transition-colors",
          isLarge ? "start-5 h-5 w-5" : "start-4 h-[18px] w-[18px]",
          isAiSearch ? "text-primary" : "text-slate-300"
        )} />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && isAiSearch) {
              e.preventDefault();
              onSearchSubmit?.();
            }
          }}
          placeholder={
            isAiSearch
              ? t("marketplace_ai_search_placeholder") || "Describe what you'd like to learn or improve..."
              : t("marketplace_search_skill_placeholder") || "What skill do you want to build today?"
          }
          className={cn(
            "w-full bg-transparent outline-none text-slate-700 placeholder:text-slate-400/70",
            isLarge ? "h-[52px] ps-14 text-[15px]" : "h-12 ps-12 text-sm",
            searchValue ? "pe-24" : "pe-16"
          )}
        />
        <div className={cn("absolute flex items-center gap-1.5", isLarge ? "end-3" : "end-2.5")}>
          {searchValue && (
            <button
              onClick={() => onSearchChange?.("")}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
              aria-label="Clear search"
            >
              <X className={cn(isLarge ? "h-4 w-4" : "h-3.5 w-3.5")} />
            </button>
          )}
          {onAiSearchToggle && (
            <button
              onClick={() => onAiSearchToggle(!isAiSearch)}
              title={isAiSearch
                ? t("switch_to_text_search") || "Switch to text search"
                : t("switch_to_ai_search") || "Switch to AI search"}
              className={cn(
                "flex items-center gap-1.5 rounded-xl font-semibold transition-all duration-300",
                isLarge ? "px-3.5 py-2 text-xs" : "px-3 py-1.5 text-[11px]",
                isAiSearch
                  ? "bg-gradient-to-r from-primary to-indigo-500 text-white shadow-sm shadow-primary/20"
                  : "bg-slate-100/80 text-slate-500 hover:bg-slate-200"
              )}
            >
              <Zap className="h-3 w-3" />
              <span>AI</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative overflow-hidden isolate shrink-0">

      {/* ── Gradient base ────────────────────────────────────────────── */}
      <div
        className="absolute inset-0 -z-10"
        style={{ background: "linear-gradient(135deg, #eef2ff 0%, #f0f4ff 30%, #faf5ff 60%, #fdf2f8 100%)" }}
      />

      {/* ── Abstract SVG illustration ────────────────────────────────── */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <svg
          className="absolute w-full h-full"
          viewBox="0 0 1200 300"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="hero-g1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(221,80%,61%)" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.06" />
            </linearGradient>
            <linearGradient id="hero-g2" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#ec4899" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="hero-g3" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0.04" />
            </linearGradient>
          </defs>

          {/* Large floating circles */}
          <circle cx="120" cy="80" r="140" fill="url(#hero-g1)" style={{ animation: "hero-drift-1 14s ease-in-out infinite" }} />
          <circle cx="1050" cy="60" r="180" fill="url(#hero-g2)" style={{ animation: "hero-drift-2 16s ease-in-out infinite" }} />
          <circle cx="700" cy="250" r="120" fill="url(#hero-g3)" style={{ animation: "hero-drift-3 12s ease-in-out infinite" }} />

          {/* Soft organic blobs */}
          <ellipse cx="350" cy="30" rx="200" ry="80" fill="hsl(221,80%,61%)" fillOpacity="0.04" transform="rotate(-15 350 30)" />
          <ellipse cx="900" cy="220" rx="160" ry="70" fill="#6366f1" fillOpacity="0.04" transform="rotate(10 900 220)" />

          {/* Decorative rings */}
          <circle cx="200" cy="200" r="60" fill="none" stroke="hsl(221,80%,61%)" strokeOpacity="0.06" strokeWidth="1.5" />
          <circle cx="200" cy="200" r="90" fill="none" stroke="hsl(221,80%,61%)" strokeOpacity="0.04" strokeWidth="1" />
          <circle cx="1000" cy="120" r="50" fill="none" stroke="#6366f1" strokeOpacity="0.06" strokeWidth="1.5" />
          <circle cx="1000" cy="120" r="75" fill="none" stroke="#6366f1" strokeOpacity="0.04" strokeWidth="1" />

          {/* Small accent dots */}
          <circle cx="300" cy="140" r="4" fill="hsl(221,80%,61%)" fillOpacity="0.15" />
          <circle cx="500" cy="50" r="3" fill="#6366f1" fillOpacity="0.12" />
          <circle cx="800" cy="180" r="5" fill="#10b981" fillOpacity="0.10" />
          <circle cx="950" cy="40" r="3" fill="#ec4899" fillOpacity="0.10" />
          <circle cx="150" cy="260" r="3" fill="#6366f1" fillOpacity="0.10" />
          <circle cx="650" cy="80" r="4" fill="hsl(221,80%,61%)" fillOpacity="0.08" />
          <circle cx="1100" cy="200" r="3" fill="#10b981" fillOpacity="0.12" />

          {/* Flowing curved lines */}
          <path d="M0,200 Q300,100 600,180 T1200,120" fill="none" stroke="hsl(221,80%,61%)" strokeOpacity="0.05" strokeWidth="1.5" />
          <path d="M0,100 Q400,200 800,80 T1200,180" fill="none" stroke="#6366f1" strokeOpacity="0.04" strokeWidth="1" />
        </svg>
      </div>

      {/* ── Subtle dot grid ──────────────────────────────────────────── */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #64748b 0.5px, transparent 0.5px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* ─── DESKTOP ────────────────────────────────────────────────────── */}
      <div className="hidden sm:block px-6 lg:px-8 py-7 lg:py-9">
        <div className="max-w-2xl mx-auto text-center">

          {/* Headline */}
          <h1 className="text-xl lg:text-2xl font-extrabold tracking-tight text-slate-800 leading-tight mb-1.5">
            {t("marketplace_hero_headline_prefix") || "Grow with expert-crafted"}{" "}
            <span
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)) 0%, #6366f1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("marketplace_hero_headline_keyword") || "activities"}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-sm text-slate-500 mb-5 max-w-lg mx-auto leading-relaxed">
            {t("marketplace_hero_headline_subtitle") || "Discover structured learning paths from real experts. Build new skills by doing, not just reading."}
          </p>

          {/* Search bar — centerpiece CTA */}
          <div className="max-w-xl mx-auto">
            {renderSearchBar("lg")}
            {isAiSearch && (
              <p className="text-[10px] mt-1.5 text-primary/50 font-medium">
                {t("ai_search_hint") || "Press Enter · Powered by semantic search"}
              </p>
            )}
          </div>

          {/* Social proof + stats strip */}
          <div className="flex items-center justify-center gap-5 mt-5">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
              <Sparkles className="w-3.5 h-3.5 text-primary/40" />
              {t("marketplace_hero_stat_expert") || "Expert-crafted activities"}
            </span>
            <span className="w-px h-3 bg-slate-200" />
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400/60" />
              {t("marketplace_hero_stat_learning") || "Learn by doing"}
            </span>
            <span className="w-px h-3 bg-slate-200" />
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
              <Users className="w-3.5 h-3.5 text-indigo-400/50" />
              {t("marketplace_hero_stat_mentoring") || "1-on-1 mentoring"}
            </span>
          </div>

          {/* Companion tip */}
          {tip && onDismissCompanion && (
            <div className="mt-4 max-w-md mx-auto">
              <CompanionBanner message={tip} onDismiss={onDismissCompanion} mini />
            </div>
          )}
        </div>
      </div>

      {/* ─── MOBILE ─────────────────────────────────────────────────────── */}
      <div className="sm:hidden px-5 py-8">
        <div className="max-w-sm mx-auto">

          {/* Badge */}
          <div className="flex justify-center mb-4">
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full"
              style={{
                background: "rgba(37,99,235,0.06)",
                border: "1px solid rgba(37,99,235,0.12)",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-widest uppercase text-primary/70">
                {t("marketplace_hero_badge") || "Discover & Learn"}
              </span>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-center text-2xl font-extrabold tracking-tight leading-tight text-slate-800 mb-2">
            {t("marketplace_hero_headline_prefix") || "Grow with expert-crafted"}{" "}
            <span
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)) 0%, #6366f1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("marketplace_hero_headline_keyword") || "activities"}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-center text-sm leading-relaxed mb-5 text-slate-500">
            {t("marketplace_hero_headline_subtitle") || "Discover structured learning paths from real experts. Build new skills by doing, not just reading."}
          </p>

          {/* Search */}
          {renderSearchBar("lg")}

          {isAiSearch && (
            <p className="text-[11px] mt-1.5 text-center text-primary/50 font-medium">
              {t("ai_search_hint") || "Press Enter · Powered by semantic search"}
            </p>
          )}

          {/* Social proof */}
          <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium">
              <Sparkles className="w-3 h-3 text-primary/40" />
              {t("marketplace_hero_stat_expert_short") || "Expert-crafted"}
            </span>
            <span className="w-px h-3 bg-slate-200" />
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium">
              <TrendingUp className="w-3 h-3 text-emerald-400/60" />
              {t("marketplace_hero_stat_learning") || "Learn by doing"}
            </span>
            <span className="w-px h-3 bg-slate-200" />
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium">
              <Users className="w-3 h-3 text-indigo-400/50" />
              {t("marketplace_hero_stat_mentoring_short") || "Mentoring"}
            </span>
          </div>

          {tip && onDismissCompanion && (
            <div className="mt-4">
              <CompanionBanner message={tip} onDismiss={onDismissCompanion} mini />
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom line ──────────────────────────────────────────────── */}
      <div className="absolute bottom-0 start-0 end-0 h-px bg-gradient-to-r from-transparent via-slate-200/60 to-transparent" />

      {/* ── Keyframes ────────────────────────────────────────────────── */}
      <style>{`
        @keyframes hero-drift-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33%       { transform: translate(30px, 15px) scale(1.05); }
          66%       { transform: translate(-15px, -10px) scale(0.95); }
        }
        @keyframes hero-drift-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%       { transform: translate(-20px, 20px) scale(1.08); }
        }
        @keyframes hero-drift-3 {
          0%, 100% { transform: translate(0, 0); }
          40%       { transform: translate(25px, -15px); }
          80%       { transform: translate(-10px, 10px); }
        }
      `}</style>
    </div>
  );
};
